import { useGetInitiativeQuestionQuery, useOverrideValueListMutation } from '@api/initiative-universal-trackers';
import { BlockingLoader } from '@g17eco/atoms/loader';
import NotFound from '@g17eco/atoms/not-found';
import { checkIsValueListColumn, checkIsValueListMetric } from '@utils/universalTracker';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { useState } from 'react';
import { Button } from 'reactstrap';
import { ValueListTable } from './ValueListTable';
import { AddOptionModal } from './AddOptionModal';
import { useToggle } from '@hooks/useToggle';
import { MergedValueListOption, ValueListConfig, ValueListOption } from '@g17eco/types/initiativeUniversalTracker';
import { UserAddedCustomOptionsModal } from './UserAddedCustomOptionsModal';
import { generateErrorToast, generateToast } from '@g17eco/molecules/toasts';

interface Props {
  initiativeId: string;
  utrId: string;
}

export const QuestionValueListManagement = ({ initiativeId, utrId }: Props) => {
  const { data, isFetching } = useGetInitiativeQuestionQuery({ initiativeId, utrId });
  const [overrideValueList, { isLoading }] = useOverrideValueListMutation();
  const [columnCode, setColumnCode] = useState<string | undefined>(undefined);
  const [addOptionModalOpen, toggleAddOptionModalOpen] = useToggle(false);
  const [addedOptionsModalOpen, toggleAddedOptionsModalOpen] = useToggle(false);
  const utr = data?.utr;
  const valueList = data?.valueList;

  if (isFetching) {
    return <BlockingLoader />;
  }

  if (!utr) {
    return <NotFound />;
  }

  if (!checkIsValueListMetric(utr)) {
    return <p>Non value list metric is not supported</p>;
  }

  const valueListColumns = utr.valueValidation?.table?.columns.filter(checkIsValueListColumn);
  const columnOptions = valueListColumns?.map((col) => ({ value: col.code, label: col.name })) ?? [];
  const selectedColumn =
    valueListColumns?.find((col) => col.code === columnCode) ??
    valueListColumns?.find((col) => col.validation?.allowCustomOptions) ??
    valueListColumns?.[0];
  const options =
    (selectedColumn
      ? valueList?.table?.columns?.find((c) => c.code === selectedColumn.code)?.options
      : valueList?.options) ?? [];
  const canCustomOption =
    utr.valueValidation?.valueList?.allowCustomOptions || selectedColumn?.validation?.allowCustomOptions;

  const updateOptions = (newOptions: MergedValueListOption[]) => {
    const valueListOption: ValueListOption[] = newOptions.map(({ code, name, hidden, isCustom }) => ({
      code,
      name: isCustom ? name : undefined, // Only keep name for custom option. Otherwise, use original name.
      hidden,
    }));
    const newValueListConfig: ValueListConfig = selectedColumn
      ? {
          table: {
            columns:
              data?.valueList?.table?.columns.map((col) =>
                col.code === selectedColumn.code ? { ...col, options: valueListOption } : col,
              ) ?? [],
          },
        }
      : {
          options: valueListOption,
        };

    overrideValueList({ initiativeId, utrId: utr._id, valueListConfig: newValueListConfig })
      .unwrap()
      .then(() => {
        generateToast({
          title: 'Dropdown options updated',
          color: 'success',
          message: 'Dropdown options have been updated',
        });
      })
      .catch((error) => {
        generateErrorToast(error);
      });
  };

  return (
    <div>
      {isLoading ? <BlockingLoader /> : null}
      <h5>{utr.name}</h5>
      <p>Dropdown settings apply globally, affecting all reports across your organisational structure.</p>
      {valueListColumns?.length ? (
        <>
          <h6 className='text-ThemeTextDark'>Select input with dropdown</h6>
          <SelectFactory
            selectType={SelectTypes.SingleSelect}
            options={columnOptions}
            value={columnOptions.find((op) => op.value === selectedColumn?.code) ?? columnOptions[0]}
            onChange={(op) => setColumnCode(op?.value ?? columnOptions[0].value)}
          />
        </>
      ) : null}

      {canCustomOption ? (
        <div className='mt-3 d-flex justify-content-end gap-2'>
          <Button color='secondary' onClick={toggleAddedOptionsModalOpen}>
            <i className='fal fa-list mr-2' />
            User added custom options
          </Button>
          <Button color='secondary' onClick={toggleAddOptionModalOpen}>
            <i className='fal fa-plus mr-2' />
            Add new option
          </Button>
        </div>
      ) : (
        <p className='text-ThemeTextMedium mt-3'>
          <i className='fa-light fa-circle-exclamation mr-2' />
          Input dropdown does not support custom options. Please contact us if you have any questions.
        </p>
      )}

      <ValueListTable options={options} updateOptions={updateOptions} />
      <AddOptionModal
        key={`AddOptionModal-${addOptionModalOpen ? 'open' : 'closed'}`}
        isOpen={addOptionModalOpen}
        toggle={toggleAddOptionModalOpen}
        options={options}
        updateOptions={updateOptions}
      />
      <UserAddedCustomOptionsModal
        key={`AddedOptionsModal-${addedOptionsModalOpen ? 'open' : 'closed'}`}
        isOpen={addedOptionsModalOpen}
        toggle={toggleAddedOptionsModalOpen}
        initiativeId={initiativeId}
        utrId={utrId}
        columnCode={selectedColumn?.code}
        options={options}
        updateOptions={updateOptions}
      />
    </div>
  );
};
